import React, { useState, useEffect } from 'react';
import { Button, message, Tabs, Typography, Space } from 'antd';
import { toastSuccess, toastError, toastWarn } from '../Toast/ToastComponent';
import { SendOutlined, RollbackOutlined } from '@ant-design/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FaArrowLeft } from 'react-icons/fa';
import { FiEdit } from 'react-icons/fi';

import style from '../../Styles/AddUser.module.css';

// Import tab components
import BasicInformation from './BasicInformation';
import Questions from './Questions';
import Governance from './Governance';
import Review from './Review';

// Import APIs
import SurveyCategoryApi from '../../Api/SurveyCategoryApi';
import SurveyPeriodApi from '../../Api/SurveyPeriodApi';
import SurveyApi from '../../Api/SurveyApi';

dayjs.extend(customParseFormat);

const { TabPane } = Tabs;
const { Title } = Typography;

const extractEntityId = (item) => {
  if (item === null || item === undefined) return null;

  if (typeof item === 'object') {
    return (
      item.id ??
      item.value ??
      item.user_id ??
      item.department_id ??
      item.governance_department_id ??
      item.member_id ??
      null
    );
  }

  return item;
};

const normalizeIds = (items = []) =>
  Array.isArray(items)
    ? items
        .map((item) => extractEntityId(item))
        .filter((value) => value !== null && value !== undefined)
    : [];

const areSameIds = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => String(value) === String(right[index]));
};

const CreateSurveyQn = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [submittingAction, setSubmittingAction] = useState(false);
  const numericId = id ? Number(id) : null;

  // Detect mode from URL path
  const isEdit = location.pathname.includes('/edit/') && numericId;
  const isView = location.pathname.includes('/view/') && numericId;
  const isPreview = location.pathname.includes('/preview/') && numericId;

  // Read-only mode if view or preview
  const readOnly = isView || isPreview;

  // Tab state
  const [activeTab, setActiveTab] = useState('1');
  const [completedTabs, setCompletedTabs] = useState(new Set(['1']));
  const [errors, setErrors] = useState({});

  // Basic Information state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [periodId, setPeriodId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [anonymousResponse, setAnonymousResponse] = useState(false);
  const [allowResubmission, setAllowResubmission] = useState(false);
  const [requireAllQuestions, setRequireAllQuestions] = useState(true);
  const [multiLanguage, setMultiLanguage] = useState([]);
  const [autoSend, setAutoSend] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);

  // Department and Users state
  const [departmentIds, setDepartmentIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [userList, setUserList] = useState([]);

  // Questions state
  const [categories, setCategories] = useState(() => {
    if (!numericId && !window.location.pathname.includes('/edit/') && !window.location.pathname.includes('/view/') && !window.location.pathname.includes('/preview/')) {
      return [{
        localId: Date.now() + Math.random(),
        categoryId: null,
        categoryName: '',
        questions: [{
          localId: Date.now() + Math.random(),
          title: '',
          scaleType: 'radio',
          required: true,
          options: [
            { title: '' },
            { title: '' },
            { title: '' },
            { title: '' },
          ],
          hasOtherOption: false,
          hasNoneOption: false,
          matrixRows: [
            { title: '' },
            { title: '' },
            { title: '' },
            { title: '' },
          ],
          matrixColumns: [{ title: '' }, { title: '' }, { title: '' }, { title: '' }],
        }],
      }];
    }
    return [];
  });
  const [categoryList, setCategoryList] = useState([]);

  // Governance state
  const [governanceDepartmentIds, setGovernanceDepartmentIds] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [approverList, setApproverList] = useState([]);
  const [approvalRequired, setApprovalRequired] = useState(false);

  // Distribution state (keeping for backward compatibility)
  const [periodList, setPeriodList] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deliveryMethod, setDeliveryMethod] = useState('webLink');
  const [allowPartial, setAllowPartial] = useState(false);
  const [sendReminders, setSendReminders] = useState(false);
  const [estimatedAudience, setEstimatedAudience] = useState(156);

  const [loading, setLoading] = useState(false);
  const [isSurveyInitializing, setIsSurveyInitializing] = useState(false);

  useEffect(() => {
    loadDropdowns();
    if (numericId) {
      loadSurveyById();
    }
  }, [numericId]);

  useEffect(() => {
    if (categoryList.length > 0 && categories.length > 0) {
      const anyMissingName = categories.some(
        (cat) => cat.categoryId && !cat.categoryName,
      );
      if (anyMissingName) {
        setCategories((prev) =>
          prev.map((cat) => {
            if (cat.categoryId && !cat.categoryName) {
              const found = categoryList.find((c) => c.id === cat.categoryId);
              return {
                ...cat,
                categoryName: found?.categoryName || found?.name || '',
              };
            }
            return cat;
          }),
        );
      }
    }
  }, [categoryList, categories.length]);

  useEffect(() => {
    if (governanceDepartmentIds && governanceDepartmentIds.length > 0) {
      loadApprovalMembers(governanceDepartmentIds);
    } else {
      setApproverList([]);
      setApprovers([]);
    }
  }, [governanceDepartmentIds]);

  const loadDropdowns = async () => {
    const cat = await SurveyCategoryApi.getDropdown({});
    const per = await SurveyPeriodApi.getDropdown({});
    const dept = await SurveyApi.getDepartmentDropdown();

    if (cat?.data?.success) setCategoryList(cat.data.result);
    if (per?.data?.success) setPeriodList(per.data.result);
    if (dept?.data?.success) setDepartmentList(dept.data.result || []);
  };

  useEffect(() => {
    if (departmentIds.length > 0 && !readOnly) {
      loadUsersByDepartment(departmentIds);
    } else if (departmentIds.length > 0 && readOnly) {
      loadUsersByDepartment(departmentIds);
    } else {
      setUserList([]);
      if (!isEdit && !readOnly) setUserIds([]);
    }
  }, [departmentIds, readOnly]);

  const loadUsersByDepartment = async (deptIds) => {
    try {
      const res = await SurveyApi.getUserDropdown(deptIds);
      if (res?.data?.success) {
        const users = res.data.result || [];
        setUserList(users);

        if (!readOnly) {
          const availableUserIds = normalizeIds(users);

          setUserIds((prev) => {
            const validSelectedUsers = normalizeIds(prev).filter((selectedId) =>
              availableUserIds.some(
                (availableId) => String(availableId) === String(selectedId),
              ),
            );

            if (validSelectedUsers.length > 0 || isSurveyInitializing) {
              return validSelectedUsers;
            }

            return validSelectedUsers; // Do not force populate
          });
        }
      }
    } catch (error) {
      console.log('User API Error:', error);
      if (!readOnly) toastError('Failed to load users');
    }
  };

  const loadSurveyById = async () => {
    try {
      setLoading(true);
      setIsSurveyInitializing(true);

      const res = await SurveyApi.getById(numericId);

      if (res?.data?.success) {
        const data = res.data.result;

        console.log('Loaded Data:', data);

        // BASIC INFO
        setTitle(data.title || '');
        setDescription(data.description || '');

        setPeriodId(data?.PeriodModel?.id || null);

        // Safely parse incoming start/end dates handling multiple formats
        const safeParse = (val) => {
          if (!val) return null;
          const parsed = dayjs(val, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD', 'YYYY-MM-DDTHH:mm:ss.SSSZ'], true);
          if (parsed.isValid()) return parsed;
          const fallback = dayjs(val);
          return fallback.isValid() ? fallback : null;
        };

        setStartDate(safeParse(data.start_date));
        setEndDate(safeParse(data.end_date));

        setAnonymousResponse(data.anonymous_response || false);
        setAllowResubmission(data.allow_resubmission || false);
        setRequireAllQuestions(
          data.require_all_questions !== undefined
            ? data.require_all_questions
            : true,
        );

        // DEPARTMENT
        setDepartmentIds(normalizeIds(data.department_ids));
        // Governance department controls approver lookup and dropdown separately
        // Fallback to department_ids if governance_department_ids is missing
        const govDepts = data.governance_department_ids?.length > 0
          ? data.governance_department_ids
          : data.department_ids;
        setGovernanceDepartmentIds(normalizeIds(govDepts));

        // USERS
        setUserIds(normalizeIds(data.user_ids));

        // APPROVERS (Backend returns approval_member instead of approve_member)
        setApprovers(normalizeIds(data.approve_member || data.approval_member));
        setApprovalRequired(data.approval_required || false);

        // ================================
        // CATEGORY + QUESTIONS + OPTIONS
        // ================================

        if (Array.isArray(data.question)) {
          const mappedCategories = data.question.map((cat) => {
            // Group flat matrix entries back into one question per unique title
            const groupedQuestions = [];
            const matrixGroups = {}; // key: title -> { question obj, rows[] }

            (cat.questions || []).forEach((q) => {
              const type =
                q.likert_scale?.question_type ||
                q.likert_scale?.type ||
                q.question_type ||
                'radio';
              const isMatrix = type === 'matrix';

              if (isMatrix) {
                const mainTitle = q.title || '';
                const subQuestion = q.likert_scale?.question || '';
                const likert = q.likert_scale || {};

                // Support both formats:
                // New: likert_scale.options = { rows: [...], columns: [...] }
                // Old: likert_scale.rows = [...], likert_scale.options = [...]
                const nestedOpts = likert.options && !Array.isArray(likert.options) ? likert.options : null;
                const matrixRowsArray = nestedOpts ? (nestedOpts.rows || []) : (likert.rows || []);
                const matrixColumnsArray = nestedOpts ? (nestedOpts.columns || []) : (Array.isArray(likert.options) ? likert.options : []);

                if (!matrixGroups[mainTitle]) {
                  matrixGroups[mainTitle] = {
                    id: q.id || null,
                    localId: Date.now() + Math.random(),
                    title: mainTitle,
                    scaleType: 'matrix',
                    required: q.required || q.is_required || false,
                    options: [],
                    matrixRows: Array.isArray(matrixRowsArray) && matrixRowsArray.length > 0
                      ? matrixRowsArray.map((r) => ({ title: typeof r === 'string' ? r : (r.title || '') }))
                      : [],
                    matrixColumns: matrixColumnsArray.map((o) => ({
                      title: typeof o === 'string' ? o : o.title || '',
                    })),
                  };
                  groupedQuestions.push(matrixGroups[mainTitle]);
                }

                if (subQuestion.trim()) {
                  matrixGroups[mainTitle].matrixRows.push({
                    title: subQuestion,
                  });
                }
              } else {
                const likertOptions = q.likert_scale?.options;
                const resolvedOptions = Array.isArray(likertOptions)
                  ? likertOptions.map((opt) => {
                      if (type === 'sliders' || type === 'slider') {
                        return {
                          title: typeof opt === 'string' ? opt : opt.title || '',
                          min: opt.min !== undefined ? opt.min : 0,
                          max: opt.max !== undefined ? opt.max : 100,
                        };
                      }
                      return {
                        title: typeof opt === 'string' ? opt : opt.title || '',
                      };
                    })
                  : [];

                groupedQuestions.push({
                  id: q.id || null,
                  localId: Date.now() + Math.random(),
                  title: q.title || '',
                  scaleType: type === 'checkbox' ? 'multiChoice' : type,
                  required: q.is_required === 1 || q.is_required === true || q.is_required === "true" || q.required === true,
                  options: resolvedOptions,
                  matrixRows: [],
                  matrixColumns: [],
                });
              }
            });

            return {
              localId: Date.now() + Math.random(),
              categoryId: cat.category_id,
              categoryName: '',
              questions: groupedQuestions,
            };
          });

          setCategories(mappedCategories);
        }

        setCompletedTabs(new Set(['1', '2', '3', '4']));

        if (data.department_ids?.length > 0) {
          await loadUsersByDepartment(normalizeIds(data.department_ids));
        }

        const govDeptsForLoad = data.governance_department_ids?.length > 0
          ? data.governance_department_ids
          : data.department_ids;
        if (govDeptsForLoad?.length > 0) {
          await loadApprovalMembers(normalizeIds(govDeptsForLoad));
        }
      }
    } catch (err) {
      if (!readOnly) {
        toastError('Failed to load survey');
      } else {
        console.error('Error loading survey for view:', err);
      }
    } finally {
      setIsSurveyInitializing(false);
      setLoading(false);
    }
  };

  const loadApprovalMembers = async (deptIds) => {
    try {
      if (!deptIds || (Array.isArray(deptIds) && deptIds.length === 0)) {
        setApproverList([]);
        return;
      }

      const ids = Array.isArray(deptIds) ? deptIds : [deptIds];
      const res = await SurveyApi.getApprovalMember(ids);

      if (res?.data?.success) {
        setApproverList(res.data.result || []);
      } else {
        setApproverList([]);
      }
    } catch (error) {
      console.log('Approval Member API Error:', error);
      setApproverList([]);
    }
  };

  const validateBasicInformation = () => {
    if (readOnly) return true;

    const newErrors = {};
    let hasError = false;

    if (!title?.trim()) {
      newErrors.title = 'Survey title is required';
      hasError = true;
    }
    if (!periodId) {
      newErrors.periodId = 'Survey period is required';
      hasError = true;
    }
    if (!startDate) {
      newErrors.startDate = 'Start date is required';
      hasError = true;
    }
    if (!endDate) {
      newErrors.endDate = 'End date is required';
      hasError = true;
    }

    if (hasError) {
      setActiveTab('1');
      toastError('Please complete all fields before submitting.');
      setTimeout(() => {
        const errorElement = document.querySelector('.ant-input-status-error, .ant-select-status-error, .error-bg-hover, .survey-error-element');
        if (errorElement) errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }

    setErrors(newErrors);
    return !hasError;
  };

  const validateQuestions = () => {
    if (readOnly) return true;

    if (categories.length === 0) {
      toastError('At least one list is required');
      return false;
    }

    let newErrors = { ...errors };
    let hasError = false;
    let missingQuestion = false;
    let missingOption = false;
    let missingCategory = false;

    for (let i = 0; i < categories.length; i++) {
      const cat = categories[i];
      if (!cat.categoryId) {
        newErrors[`category_${cat.localId}`] = true;
        hasError = true;
        missingCategory = true;
      }
      if (!cat.questions || cat.questions.length === 0) {
        toastError('Please complete all fields before submitting.');
        return false;
      }

      for (let j = 0; j < cat.questions.length; j++) {
        const q = cat.questions[j];
        if (!q.title?.trim()) {
          newErrors[`question_${q.localId}`] = true;
          hasError = true;
          missingQuestion = true;
        }

        const type = q.scaleType;
        if (['radio', 'checkbox', 'multiChoice'].includes(type) && q.options) {
          q.options.forEach((opt, idx) => {
            if (!opt.title?.trim()) {
              newErrors[`option_${q.localId}_${idx}`] = true;
              hasError = true;
              missingOption = true;
            }
          });
        }

        if (type === 'matrix') {
          (q.matrixRows || []).forEach((row, idx) => {
            if (!row.title?.trim()) {
              newErrors[`matrixRow_${q.localId}_${idx}`] = true;
              hasError = true;
              missingOption = true;
            }
          });
          (q.matrixColumns || []).forEach((col, idx) => {
            if (!col.title?.trim()) {
              newErrors[`matrixCol_${q.localId}_${idx}`] = true;
              hasError = true;
              missingOption = true;
            }
          });
        }
      }
    }

    if (hasError) {
      setActiveTab('2');
      setErrors(newErrors);
      toastError('Please complete all fields before submitting.');
      setTimeout(() => {
        const errorElement = document.querySelector('.ant-input-status-error, .ant-select-status-error, .error-bg-hover, .survey-error-element');
        if (errorElement) errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (readOnly) {
      const next = String(Number(activeTab) + 1);
      if (next <= '4') setActiveTab(next);
      return;
    }

    let isValid = false;

    if (activeTab === '1') {
      isValid = validateBasicInformation();
      if (isValid) {
        setCompletedTabs((prev) => new Set([...prev, '1']));
        setActiveTab('2');
      }
    } else if (activeTab === '2') {
      isValid = validateQuestions();
      if (isValid) {
        setCompletedTabs((prev) => new Set([...prev, '2']));
        setActiveTab('3');
      }
    } else if (activeTab === '3') {
      setCompletedTabs((prev) => new Set([...prev, '3']));
      setActiveTab('4');
    }
  };

  const handlePrevious = () => {
    const prevTab = String(Number(activeTab) - 1);
    if (prevTab >= '1') {
      setActiveTab(prevTab);
    }
  };

  const buildPayload = (status) => {
    const resolvedUserIds =
      userIds.length > 0 ? userIds : normalizeIds(userList);

    const formattedCategories = categories.map((cat) => {
      // Flatten matrix questions: one entry per matrixRow sub-question
      const flatQuestions = [];

      cat.questions.forEach((q) => {
        const scaleTypeMap = {
          multiChoice: 'checkbox',
          radio: 'radio',
          matrix: 'matrix',
          rating: 'rating',
          sliders: 'sliders',
          freeText: 'text',
          text: 'text',
        };
        const mappedType = scaleTypeMap[q.scaleType] || 'text';

        if (q.scaleType === 'matrix') {
          // Shared options (columns) for all sub-questions
          const sharedColumns = (q.matrixColumns || [])
            .filter((c) => c.title && c.title.trim())
            .map((c) => ({ title: c.title.trim() }));

          const sharedRows = (q.matrixRows || [])
            .filter((r) => r.title && r.title.trim())
            .map((r) => ({ title: r.title.trim() }));

          flatQuestions.push({
            title: q.title,
            is_required: q.required,
            likert_scale: {
              type: 'matrix',
              options: {
                rows: sharedRows,
                columns: sharedColumns,
              },
            },
          });
        } else {
          flatQuestions.push({
            title: q.title,
            is_required: q.required === true,
            likert_scale: {
              question_type: mappedType,
              options:
                q.scaleType === 'freeText'
                  ? []
                  : q.scaleType === 'sliders'
                    ? (q.options || []).map((opt) => ({
                        min: opt.min !== undefined ? opt.min : 0,
                        max: opt.max !== undefined ? opt.max : 100,
                      }))
                    : (q.options || [])
  .filter((opt) => {
    const title = typeof opt.title === 'string' ? opt.title : String(opt.title || '');
    return title.trim() !== '';
  })
  .map((opt) => {
    const title = typeof opt.title === 'string' ? opt.title : String(opt.title || '');
    return { title: title.trim() };
  })
            },
          });
        }
      });

      return {
        category_id: cat.categoryId,
        questions: flatQuestions,
      };
    });

    return {
      id: numericId,
      title,
      description,
      status,

      period_id: periodId,

      start_date: startDate ? (dayjs.isDayjs(startDate) ? startDate.format('YYYY-MM-DD') : dayjs(startDate, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'MM/DD/YYYY']).format('YYYY-MM-DD')) : null,

      end_date: endDate ? (dayjs.isDayjs(endDate) ? endDate.format('YYYY-MM-DD') : dayjs(endDate, ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'MM/DD/YYYY']).format('YYYY-MM-DD')) : null,

      require_all_questions: requireAllQuestions,

      department_ids: departmentIds && departmentIds.length > 0 ? departmentIds : departmentList.map((d) => d.value),
      user_ids: resolvedUserIds,

      approve_member: approvers,
      governance_department_ids: governanceDepartmentIds,

      list: formattedCategories,
    };
  };

  const handleSaveDraft = async () => {
    if (readOnly) return;

    if (!title?.trim() || !periodId || !startDate || !endDate) {
      toastWarn('Please fill in required fields (Title, Period, Dates)');
      return;
    }

    const payload = buildPayload(false);

    try {
      setSubmittingAction(true);
      let res;
      if (isEdit) {
        res = await SurveyApi.update(payload);
      } else {
        res = await SurveyApi.create(payload);
      }

      if (res?.data?.success) {
        toastSuccess('Draft saved successfully');
        navigate('/survey');
      } else {
        toastError(res?.data?.message || 'Failed to save draft');
      }
    } catch (err) {
      toastError('Server Error');
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleSend = async () => {
    if (readOnly) return;

    if (!validateBasicInformation() || !validateQuestions()) return;

    const payload = buildPayload(true);

    try {
      setSubmittingAction(true);
      let res;

      if (isEdit) {
        res = await SurveyApi.update(payload);
      } else {
        res = await SurveyApi.create(payload);
      }

      if (res?.data?.success) {
        toastSuccess(
          isEdit
            ? 'Survey Updated Successfully'
            : 'Survey Created Successfully',
        );

        navigate('/survey');
      } else {
        toastError(res?.data?.message || 'Something went wrong');
      }
    } catch (error) {
      toastError('Server Error');
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleBackToList = () => {
    navigate('/survey');
  };

  const stepIndicatorStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 24,
    padding: '16px 24px',
    background: '#f9f9f9',
    borderRadius: 8,
  };

  const stepItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
  };

  const stepCircleStyle = (isActive, isCompleted) => ({
    width: 32,
    height: 32,
    borderRadius: 16,
    background: isActive ? '#134780' : isCompleted ? '#52c41a' : '#e6e6e6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
    color: isActive || isCompleted ? '#fff' : '#999',
  });

  const renderStepIndicator = () => {
    const steps = ['Basic Information', 'Questions', 'Governance', 'Review'];

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          marginBottom: 24,
          padding: '14px 20px',
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e8edf2',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          flexWrap: 'wrap',
        }}
      >
        {steps.map((step, index) => {
          const stepNum = String(index + 1);
          // Map the 5-label stepper to 4 actual tabs (Distribution = tab 4 = Review in our flow)
          const tabNum = index < 4 ? stepNum : '4';
          const isActive = activeTab === tabNum && index < 4;
          const isCompleted = index < 4 && completedTabs.has(stepNum) && activeTab !== stepNum;
          const isFuture = !isCompleted && !isActive;

          return (
            <div
              key={step}
              onClick={() => {
                if (index < 4) {
                  if (readOnly || isEdit || completedTabs.has(stepNum)) {
                    setActiveTab(stepNum);
                  }
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor:
                  index < 4 && (readOnly || isEdit || completedTabs.has(stepNum))
                    ? 'pointer'
                    : 'default',
              }}
            >
              {/* Step Pill */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 18px 8px 14px',
                  borderRadius: 24,
                  background: isCompleted
                    ? '#e8eef6'
                    : isActive
                    ? '#e8eef6'
                    : 'transparent',
                  fontWeight: isActive ? 700 : 400,
                  fontSize: 14,
                  color: isCompleted
                    ? '#134780'
                    : isActive
                    ? '#134780'
                    : '#888',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s',
                }}
              >
                {/* Circle icon */}
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: isCompleted || isActive ? '#134780' : '#d9dee3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {isCompleted ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : isActive ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <span style={{ fontSize: 11, color: '#fff', fontWeight: 600 }}>{index + 1}</span>
                  )}
                </div>
                <span>{step}</span>
              </div>

              {/* Chevron separator */}
              {index < steps.length - 1 && (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  style={{ flexShrink: 0, margin: '0 2px' }}
                >
                  <path
                    d="M6 3l6 6-6 6"
                    stroke="#c8d0d8"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const getPageTitle = () => {
    if (isPreview) return 'Preview Survey';
    if (isView) return 'View Survey';
    if (isEdit) return 'Edit Survey';
    return 'Create Survey';
  };

  return (
    <div>
      <div className="d-flex px-0 pb-1 align-items-center" style={{ marginBottom: '20px' }}>
        <div
          className={`${style['arrow-icon']} border px-2 pb-1`}
          onClick={() => navigate(-1)}
          style={{ cursor: 'pointer' }}
        >
          <FaArrowLeft />
        </div>
        <div className={`mx-3 ${style['add-user']} mb-0`}>{getPageTitle()}</div>
        
        {isPreview && (
          <div 
            title="Edit Survey"
            onClick={() => navigate(`/survey/edit/${numericId}`)} 
            style={{ 
              marginLeft: 'auto', 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer', 
              color: '#333', 
              padding: '6px 10px', 
              borderRadius: '4px', 
              border: '1px solid #ccc',
              background: '#fafafa',
              transition: 'background 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#fafafa'}
          >
             <FiEdit size={16} />
             <span style={{ marginLeft: 6, fontSize: 14, fontWeight: 500 }}>Edit</span>
          </div>
        )}
      </div>

      {renderStepIndicator()}

      <Tabs
        activeKey={activeTab}
        onChange={readOnly ? null : setActiveTab}
        tabBarStyle={{ display: 'none' }}
      >
        <TabPane tab="Basic Information" key="1">
          <BasicInformation
            title={title}
            setTitle={setTitle}
            description={description}
            setDescription={setDescription}
            periodId={periodId}
            setPeriodId={setPeriodId}
            periodList={periodList}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            anonymousResponse={anonymousResponse}
            setAnonymousResponse={setAnonymousResponse}
            allowResubmission={allowResubmission}
            setAllowResubmission={setAllowResubmission}
            requireAllQuestions={requireAllQuestions}
            setRequireAllQuestions={setRequireAllQuestions}
            multiLanguage={multiLanguage}
            setMultiLanguage={setMultiLanguage}
            autoSend={autoSend}
            setAutoSend={setAutoSend}
            scheduleDate={scheduleDate}
            setScheduleDate={setScheduleDate}
            departmentIds={departmentIds}
            setDepartmentIds={(val) => {
              setDepartmentIds(val);
              setGovernanceDepartmentIds(val);
            }}
            userIds={userIds}
            setUserIds={setUserIds}
            departmentList={departmentList}
            userList={userList}
            errors={errors}
            setErrors={setErrors}
            isPreview={readOnly}
          />
        </TabPane>

        <TabPane tab="Questions" key="2">
          <Questions
            categories={categories}
            setCategories={setCategories}
            categoryList={categoryList}
            errors={errors}
            setErrors={setErrors}
            isPreview={readOnly}
          />
        </TabPane>

        <TabPane tab="Governance" key="3">
          <Governance
            departmentIds={governanceDepartmentIds}
            setDepartmentIds={setGovernanceDepartmentIds}
            departmentList={departmentList}
            approverList={approverList}
            approvers={approvers}
            setApprovers={setApprovers}
            approvalRequired={approvalRequired}
            setApprovalRequired={setApprovalRequired}
            requireAllQuestions={requireAllQuestions}
            setRequireAllQuestions={setRequireAllQuestions}
            anonymousResponse={anonymousResponse}
            setAnonymousResponse={setAnonymousResponse}
            allowResubmission={allowResubmission}
            setAllowResubmission={setAllowResubmission}
            isPreview={readOnly}
          />
        </TabPane>

        <TabPane tab="Review" key="4">
          <Review
            title={title}
            description={description}
            periodId={periodId}
            periodList={periodList}
            startDate={startDate}
            endDate={endDate}
            anonymousResponse={anonymousResponse}
            allowResubmission={allowResubmission}
            requireAllQuestions={requireAllQuestions}
            departmentIds={departmentIds}
            departmentList={departmentList}
            userIds={userIds}
            userList={userList}
            approvers={approvers}
            approverList={approverList}
            contacts={contacts}
            estimatedAudience={estimatedAudience}
            categories={categories}
          />
        </TabPane>
      </Tabs>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 24,
        }}
      >
        <div>
          {activeTab !== '1' && (
            <Button icon={<RollbackOutlined />} onClick={handlePrevious}>
              Previous
            </Button>
          )}
        </div>
        <div>
          <Space>
            {readOnly && activeTab === '4' && (
              <Button onClick={handleBackToList}>Back to List</Button>
            )}

            {!readOnly && (
              <>
                {/* <Button icon={<SaveOutlined />} onClick={handleSaveDraft}>
                  Save Draft
                </Button> */}
                {isEdit && activeTab !== '4' && (
                  <Button
                    type="primary"
                    onClick={handleSend}
                    loading={submittingAction}
                    style={{ marginRight: 8 }}
                  >
                    Update
                  </Button>
                )}
              </>
            )}

            {activeTab !== '4' ? (
              <Button type="primary" onClick={handleNext}>
                Next
              </Button>
            ) : (
              !readOnly && (
                <Button
                  type="primary"
                  // icon={<SendOutlined />}
                  onClick={handleSend}
                  loading={submittingAction}
                >
                  {isEdit ? 'Update' : 'Submit'}
                </Button>
              )
            )}
          </Space>
        </div>
      </div>
    </div>
  );
};

export default CreateSurveyQn;
